import React from 'react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import ReactSelect from 'react-select'
import BaseQuestion from '../base-question'

interface DropdownQuestionProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    placeholder?: string
    responses?: Array<{
      id: number | string
      text: string
    }>
  }
  // RHF mode
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode
  value?: number | string
  error?: string
  onChange?: (value: number | string | undefined) => void
}

const DropdownQuestion: React.FC<DropdownQuestionProps> = ({
  question,
  control,
  errors,
  value,
  error,
  onChange,
}) => {
  const isRHFMode = !!control && !!errors

  // Convert responses to options format expected by react-select
  const options =
    question.responses?.map(response => ({
      value: String(response.id),
      label: response.text,
    })) || []

  const questionId =
    typeof question.id === 'string' ? parseInt(question.id, 10) : question.id

  if (isRHFMode) {
    // RHF mode - use Controller
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        control={control}
        errors={errors}>
        <Controller
          name={`question_${question.id}`}
          control={control}
          rules={{
            required: question.isRequired ? 'This field is required' : false,
          }}
          render={({ field }) => (
            <ReactSelect
              options={options}
              value={options.find(option => option.value === field.value)}
              onChange={selectedOption =>
                field.onChange(
                  selectedOption ? selectedOption.value : undefined
                )
              }
              placeholder={question.placeholder || 'Select an option'}
              isClearable={false}
              styles={{
                control: (base, props) => ({
                  ...base,
                  background: '#fff',
                  border: '2px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  minHeight: '44px',
                  boxShadow: props.isFocused
                    ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
                    : 'none',
                  borderColor: props.isFocused ? '#3b82f6' : '#e2e8f0',
                  ':hover': {
                    borderColor: props.isFocused ? '#3b82f6' : '#cbd5e0',
                  },
                }),
                valueContainer: base => ({
                  ...base,
                  padding: '0.5rem 0.75rem',
                }),
                input: base => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }),
                indicatorSeparator: () => ({
                  display: 'none',
                }),
                dropdownIndicator: base => ({
                  ...base,
                  color: '#6b7280',
                  ':hover': {
                    color: '#374151',
                  },
                }),
                option: (styles, { isFocused, isSelected }) => ({
                  ...styles,
                  backgroundColor: isSelected
                    ? '#3b82f6'
                    : isFocused
                    ? '#eff6ff'
                    : '#fff',
                  color: isSelected ? '#fff' : '#2d3748',
                  cursor: 'pointer',
                  ':active': {
                    ...styles[':active'],
                    backgroundColor: isSelected ? '#2563eb' : '#dbeafe',
                  },
                }),
                menu: base => ({
                  ...base,
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }),
              }}
            />
          )}
        />
      </BaseQuestion>
    )
  } else {
    // Zustand mode - use direct props
    return (
      <BaseQuestion
        question={{
          ...question,
          id: questionId,
        }}
        error={error}>
        <ReactSelect
          options={options}
          value={options.find(option => option.value === String(value))}
          onChange={selectedOption =>
            onChange?.(
              selectedOption
                ? typeof question.responses?.[0]?.id === 'number'
                  ? Number(selectedOption.value)
                  : selectedOption.value
                : undefined
            )
          }
          placeholder={question.placeholder || 'Select an option'}
          isClearable={false}
          styles={{
            control: (base, props) => ({
              ...base,
              background: '#fff',
              border: '2px solid #e2e8f0',
              borderRadius: '0.5rem',
              minHeight: '44px',
              boxShadow: props.isFocused
                ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
                : 'none',
              borderColor: props.isFocused ? '#3b82f6' : '#e2e8f0',
              ':hover': {
                borderColor: props.isFocused ? '#3b82f6' : '#cbd5e0',
              },
            }),
            valueContainer: base => ({
              ...base,
              padding: '0.5rem 0.75rem',
            }),
            input: base => ({
              ...base,
              margin: 0,
              padding: 0,
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
            dropdownIndicator: base => ({
              ...base,
              color: '#6b7280',
              ':hover': {
                color: '#374151',
              },
            }),
            option: (styles, { isFocused, isSelected }) => ({
              ...styles,
              backgroundColor: isSelected
                ? '#3b82f6'
                : isFocused
                ? '#eff6ff'
                : '#fff',
              color: isSelected ? '#fff' : '#2d3748',
              cursor: 'pointer',
              ':active': {
                ...styles[':active'],
                backgroundColor: isSelected ? '#2563eb' : '#dbeafe',
              },
            }),
            menu: base => ({
              ...base,
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }),
          }}
        />
      </BaseQuestion>
    )
  }
}

export default DropdownQuestion
